import { Link, useParams } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import BookButton from '@/pages/home/components/BookButton';
import BookingCta from '@/components/feature/BookingCta';
import NotFound from '@/pages/NotFound';
import { looks, lookDetails, lookSlugs } from '@/mocks/home';

const linkCls = 'inline-flex items-center gap-2 text-[14px] font-medium text-foreground-950 border-b border-foreground-950 pb-0.5 whitespace-nowrap cursor-pointer hover:text-foreground-600';

export default function WorkDetailPage() {
  const { slug } = useParams();
  const idx = lookSlugs.indexOf(slug ?? '');
  if (idx < 0) return <NotFound />;
  const look = looks[idx];
  const detail = lookDetails.find((d) => d.title === look.title);
  const serviceHref = detail?.service === 'Creative Colour' ? '/services#colour' : '/services#cut';
  const prev = (idx + looks.length - 1) % looks.length;
  const next = (idx + 1) % looks.length;
  const others = looks.map((l, i) => ({ ...l, i })).filter((l) => l.i !== idx).slice(0, 3);

  return (
    <div className="bg-background-50 overflow-x-hidden min-h-screen">
      <Header />
      <main>
        <section className="bg-background-50 pt-28 md:pt-36 pb-16 md:pb-24">
          <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16">
            <Link to="/work" className="inline-flex items-center gap-2 text-[13px] text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span> All work
            </Link>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-10">
              <div className="lg:col-span-6 w-full aspect-[4/5] overflow-hidden bg-background-100">
                <img src={look.src} alt={`${look.title} hairstyle — ${look.tag}`} title={`${look.title} — ${look.tag}`} width={800} height={1000} className="w-full h-full object-cover object-top" />
              </div>
              <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
                <p className="text-[13px] text-foreground-600">{detail?.category}</p>
                <h1 className="mt-3 font-heading font-semibold tracking-[-0.03em] leading-[1] text-[44px] md:text-[64px] text-foreground-950">{look.title}</h1>
                <p className="mt-6 text-[16px] leading-relaxed text-foreground-700">{detail?.desc}</p>
                <dl className="mt-8 border-t border-foreground-950 text-[15px]">
                  {[['Technique', look.tag.split(' · ')[1] ?? look.tag], ['Related service', detail?.service ?? '']].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[140px_1fr] gap-4 py-4 border-b border-background-300">
                      <dt className="text-foreground-600">{k}</dt><dd className="text-foreground-950">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                  <BookButton />
                  <Link to={serviceHref} className={linkCls}>
                    {detail?.service} pricing <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>
                <p className="mt-6 text-[13px] text-foreground-600">Image is illustrative of our style, not a client result.</p>
                <div className="mt-10 pt-6 border-t border-background-300 flex justify-between gap-4 text-[14px]">
                  <Link to={`/work/${lookSlugs[prev]}`} className="inline-flex items-center gap-2 text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>{looks[prev].title}
                  </Link>
                  <Link to={`/work/${lookSlugs[next]}`} className="inline-flex items-center gap-2 text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer">
                    {looks[next].title}<span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-t border-background-300 py-16 md:py-24">
          <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16">
            <h2 className="font-heading font-semibold tracking-[-0.02em] text-[32px] md:text-[44px] leading-[1.05] text-foreground-950">More work</h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
              {others.map((l) => (
                <Link key={l.title} to={`/work/${lookSlugs[l.i]}`} className="block cursor-pointer group">
                  <div className="w-full aspect-[4/5] overflow-hidden bg-background-50">
                    <img src={l.src} alt={`${l.title} hairstyle — ${l.tag}`} title={`${l.title} — ${l.tag}`} width={800} height={1000} loading="lazy" className="w-full h-full object-cover object-top" />
                  </div>
                  <h3 className="mt-4 text-[18px] font-semibold text-foreground-950 group-hover:text-foreground-600">{l.title}</h3>
                  <p className="mt-1 text-[14px] text-foreground-600">{l.tag}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <BookingCta title="Inspired by this look?" text="Bring it to your consultation and we'll plan a version that suits your hair." />
      </main>
      <Footer />
    </div>
  );
}